import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { buildPractice, quizStars, uasaSubjectName, type PracticeItem, type UasaBlueprint, type UasaQuestion } from "@jomcerdik/shared";
import { api, errorMessage } from "../lib/api";
import { t } from "../lib/i18n";
import { useMe } from "../hooks/useMe";
import { Alert } from "../components/Form";
import { Mascot } from "../ui/Art";
import { StarRow } from "../ui/Icon";
import { UasaDiagram } from "../ui/UasaDiagram";
import { BigButton, BigLink, Hero } from "../ui/kit";

/* Online practice for a generated UASA paper: preview → answer (objective questions) → check → result.
 * Reads the paper through GET /api/exams/:id; buildPractice() turns every question into A/B/C items. Long written answers are optional typed items. */

type Paper = { kind?: string; practice?: Record<string, string[]>; blueprint: UasaBlueprint; questions: UasaQuestion[] };
type Exam = { id: string; status: string; settings_json: { subject?: string; year?: number }; paper_json: Paper };

const LETTERS = ["A", "B", "C", "D"];
const img = (examId: string, key?: string | null) => (key ? `/api/exams/${examId}/images/${key}` : null);
const saveResult = (id: string, score: number, total: number) => { try { localStorage.setItem(`uasa.result.${id}`, JSON.stringify({ score, total, at: new Date().toISOString() })); } catch { /* optional */ } };

/** Pictures that belong to a question stem (a scene or a picture strip), when the paper has them. */
function stemImages(examId: string, q: UasaQuestion): string[] {
  const d = q.diagram;
  if (!d) return [];
  if (d.type === "scene") return [img(examId, d.imageId)].filter((x): x is string => !!x);
  if (d.type === "picture") return d.items.map((i) => img(examId, i.imageId)).filter((x): x is string => !!x);
  return [];
}
const needsPdf = (q: UasaQuestion) => !!q.diagram && q.diagram.type === "frame";

function Stem({ examId, q, item }: { examId: string; q: UasaQuestion; item: PracticeItem }) {
  const imgs = stemImages(examId, q);
  return (
    <>
      {item.passage && <p className="mb-2 rounded-2xl bg-white/70 p-3 text-sm text-slate-700">{item.passage}</p>}
      <p className="text-xl font-black leading-snug text-slate-900" dir="auto">{item.stem}</p>
      {item.diagram && <UasaDiagram d={item.diagram} examId={examId} />}
      {imgs.length > 0 && <div className="my-3 flex flex-wrap justify-center gap-2">{imgs.map((src) => <img key={src} src={src} alt="" className="h-28 w-28 rounded-2xl bg-white object-contain sm:h-36 sm:w-36" />)}</div>}
      {item.wordBank && item.wordBank.length > 0 && <p className="mt-2 text-sm text-slate-600" dir="auto">{item.wordBank.join(" · ")}</p>}
      {needsPdf(q) && <p className="mt-2 text-xs text-slate-500">{t("Ruang melukis ada dalam PDF kertas.")}</p>}
    </>
  );
}

export function UasaPaperPage() {
  const id = useParams().id ?? "";
  const { me, loading } = useMe();
  const [exam, setExam] = useState<Exam | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"preview" | "answer" | "result">("preview");
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [typedText, setTypedText] = useState("");
  const [shown, setShown] = useState(false);
  const [n, setN] = useState(0);

  const [extra, setExtra] = useState<Record<string, string[]> | null>(null);
  useEffect(() => { if (me) api<{ exam: Exam }>(`/api/exams/${id}`).then((r) => setExam(r.exam)).catch((e) => setError(errorMessage(e))); }, [id, me?.email]);
  // Wrong options for worded answers come from the server (written once, then cached on the paper).
  useEffect(() => {
    if (!exam) return;
    if (exam.paper_json.practice) { setExtra(exam.paper_json.practice); return; }
    api<{ distractors: Record<string, string[]> }>(`/api/exams/${id}/practice`, { method: "POST", json: {} }).then((r) => setExtra(r.distractors)).catch(() => setExtra({}));
  }, [exam?.id]);
  const items = useMemo(() => buildPractice(exam?.paper_json.questions ?? [], extra ?? {}), [exam, extra]);
  const objective = items; // every item is answerable; typed ones are optional and not scored
  const scored = useMemo(() => items.filter((x) => !x.typed), [items]);

  if (loading) return <p className="text-sm text-muted">{t("Memuatkan…")}</p>;
  if (!me) return <Alert kind="info"><Link to={`/login?next=/uasa/kertas/${id}`} className="underline">{t("Log masuk")}</Link> {t("untuk menjana kertas.")}</Alert>;
  if (error) return <Alert kind="error">{error}</Alert>;
  if (!exam || extra === null) return <p className="text-sm text-muted">{t("Menyediakan pilihan jawapan…")}</p>;
  const s = exam.settings_json;
  const title = `${uasaSubjectName(s.subject ?? "")} · ${t("Tahun {n}", { n: s.year ?? 0 })}`;
  const isRight = (x: PracticeItem, i: number) => !x.typed && answers[i] === String(x.answer);
  const score = objective.filter(isRight).length;

  if (mode === "preview") {
    return (
      <>
        <Link to="/uasa" className="mb-3 inline-flex min-h-10 items-center rounded-full bg-card px-4 text-sm font-bold text-slate-700 ring-1 ring-black/10">‹ UASA</Link>
        <Hero title={title} text={t("Pratonton kertas. {n} soalan boleh dijawab terus dalam talian (pilih A, B, C).", { n: scored.length })} pose="boy-reading">
          {objective.length > 0 && <BigButton tone="green" onClick={() => { setMode("answer"); setN(0); setAnswers({}); }}>{t("Mula latihan")}</BigButton>}
        </Hero>
        <ol className="space-y-3">
          {exam.paper_json.questions.map((q) => {
            const its = items.filter((x) => x.ref.startsWith(`${q.section}${q.number}`) && exam.paper_json.questions[x.q] === q);
            return (
              <li key={`${q.section}${q.number}`} className="pastel-blue rounded-3xl p-4">
                <p className="mb-1 text-xs font-bold text-slate-500">{q.section}{q.number} · {q.marks}m</p>
                {its[0] ? <Stem examId={id} q={q} item={its[0]} /> : <p className="text-xl font-black text-slate-900" dir="auto">{q.stemMs}</p>}
                {its.length > 1 && <p className="mt-1 text-xs text-slate-500">{t("{n} bahagian", { n: its.length })}</p>}
              </li>
            );
          })}
        </ol>
        {items.some((x) => x.typed) && <Alert kind="info">{t("Soalan menulis panjang boleh ditaip, tetapi tidak digalakkan. Untuk latihan menulis, jana kertas UASA atau DSKP (PDF) dan jawab di atas kertas.")} <Link to="/uasa" className="font-bold underline">{t("Jana kertas")}</Link></Alert>}
      </>
    );
  }

  if (mode === "answer") {
    const q = objective[n]!;
    const qq = exam.paper_json.questions[q.q]!;
    const picked = answers[n];
    const done = q.typed ? shown : picked !== undefined;
    const next = () => {
      if (n + 1 >= objective.length) { saveResult(id, objective.filter(isRight).length, scored.length); setMode("result"); } else { setN(n + 1); setShown(false); setTypedText(""); }
    };
    return (
      <>
        <div className="mb-3 flex items-center gap-3">
          <button type="button" onClick={() => setMode("preview")} aria-label={t("Tutup")} className="grid h-11 w-11 place-items-center rounded-full bg-card text-lg ring-1 ring-black/10">✕</button>
          <div className="h-3 flex-1 overflow-hidden rounded-full bg-black/10"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${(n / objective.length) * 100}%` }} /></div>
          <span className="text-sm font-bold text-slate-600">{n + 1}/{objective.length}</span>
        </div>
        <p className="mb-1 text-xs font-bold text-slate-500">{q.ref} · {q.marks}m</p>
        <div className="pastel-blue mb-4 rounded-[28px] p-4"><Stem examId={id} q={qq} item={q} /></div>
        {q.typed ? (
          <div>
            <Alert kind="info">{t("Jawapan menulis panjang tidak digalakkan di sini. Lebih baik jana kertas UASA atau DSKP (PDF) untuk latihan bertulis.")} <Link to="/uasa" className="font-bold underline">{t("Jana kertas")}</Link></Alert>
            <textarea value={typedText} onChange={(e) => setTypedText(e.target.value)} rows={4} aria-label={t("Jawapan anda")} className="mt-3 w-full rounded-2xl bg-card p-3 text-lg ring-2 ring-black/10" dir="auto" />
            {!shown && <div className="mt-3 flex justify-center"><BigButton tone="soft" onClick={() => setShown(true)}>{t("Tunjuk jawapan contoh")}</BigButton></div>}
            {shown && <p className="mt-3 rounded-2xl bg-emerald-50 p-3 text-slate-900" role="status" dir="auto"><b>{t("Jawapan contoh")}:</b> {q.model}</p>}
          </div>
        ) : (
          <div className={`grid gap-3 ${q.tf ? "grid-cols-2" : "sm:grid-cols-2"}`}>
            {q.options.map((o, i) => {
              const letter = LETTERS[i]!;
              const right = i === q.answer;
              const tone = !done ? "ring-black/5" : right ? "ring-emerald-500 bg-emerald-50" : picked === String(i) ? "ring-red-400 bg-red-50 animate-shake" : "opacity-50 ring-black/5";
              const src = img(id, q.optionImageIds?.[i]);
              return (
                <button key={i} type="button" disabled={done} onClick={() => setAnswers((a) => ({ ...a, [n]: String(i) }))} aria-label={letter}
                  className={`flex min-h-20 items-center gap-3 rounded-3xl bg-card p-3 text-left shadow-sm ring-2 transition active:scale-95 ${tone}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand text-lg font-black text-white">{letter}</span>
                  {src ? <img src={src} alt="" className="h-24 w-24 object-contain" /> : <span className="text-lg font-bold text-slate-900" dir="auto">{q.tf ? t(o) : o}</span>}
                </button>
              );
            })}
          </div>
        )}
        {done && (
          <div className="mt-4 text-center">
            {!q.typed && <p className="text-xl font-black text-slate-900" role="status">{picked === String(q.answer) ? t("Betul! Bagus!") : `${t("Jawapan betul")}: ${LETTERS[q.answer]}. ${q.options[q.answer] ?? ""}`}</p>}
            <div className="mt-3 flex justify-center"><BigButton tone="green" onClick={next}>{n + 1 >= objective.length ? t("Lihat keputusan") : t("Seterusnya")} ›</BigButton></div>
          </div>
        )}
      </>
    );
  }

  const stars = quizStars(score, scored.length);
  return (
    <>
      <div className="pastel-green mx-auto max-w-lg rounded-[32px] p-6 text-center">
        <Mascot pose={stars >= 2 ? "boy-cheer" : "girl-cheer"} className="mx-auto h-40 w-40" />
        <h1 className="mt-2 text-3xl font-black text-slate-900">{score}/{scored.length}</h1>
        <p className="text-lg text-slate-700">{title}</p>
        <p className="my-2 flex justify-center"><StarRow n={stars} className="h-11 w-11" /></p>
        <div className="flex flex-wrap justify-center gap-3">
          <BigButton tone="green" onClick={() => { setMode("answer"); setN(0); setAnswers({}); }}>{t("Cuba lagi")}</BigButton>
          <BigLink tone="soft" to="/uasa">UASA</BigLink>
        </div>
      </div>
      <h2 className="mb-2 mt-8 text-xl font-black text-slate-900">{t("Semakan jawapan")}</h2>
      <ol className="space-y-2">
        {objective.map((q, i) => {
          if (q.typed) return <li key={i} className="rounded-2xl bg-slate-50 p-3 text-sm"><p className="font-bold text-slate-900" dir="auto">{q.ref}. {q.stem}</p><p className="text-slate-700" dir="auto">{t("Jawapan contoh")}: {q.model}</p></li>;
          const ok = isRight(q, i);
          const mine = answers[i] === undefined ? "–" : q.options[Number(answers[i])];
          return (
            <li key={i} className={`rounded-2xl p-3 text-sm ${ok ? "bg-emerald-50" : "bg-red-50"}`}>
              <p className="font-bold text-slate-900" dir="auto">{q.ref}. {q.stem}</p>
              <p className="text-slate-700" dir="auto">{t("Jawapan anda")}: {mine} · {t("Jawapan betul")}: {q.options[q.answer]} {ok ? "✓" : "✗"}</p>
            </li>
          );
        })}
      </ol>
      {items.some((x) => x.typed) && <p className="mt-4 text-sm text-slate-600">{t("Untuk soalan menulis panjang, jana kertas UASA atau DSKP (PDF).")} <Link to="/uasa" className="font-bold underline">{t("Jana kertas")}</Link></p>}
    </>
  );
}
